// Play / How to Play card: before a level begins, over a mask on the whole
// screen. A constellation of STARS in progress (S-T-A-R selected, a pointer
// over the last S), two lines of text, "Don't show this again" and
// Understood, which takes the card and mask away and begins the level.
import { useState } from 'react';
import { Button } from '../components/Button';

/** Cassiopeia's W: five stars for the five letters of STARS. */
const STARS = [
  { letter: 'S', x: 44, y: 62 },
  { letter: 'T', x: 108, y: 128 },
  { letter: 'A', x: 170, y: 74 },
  { letter: 'R', x: 236, y: 132 },
  { letter: 'S', x: 300, y: 52 },
] as const;
const SELECTED = 4;
const R = 19;
/** The card's fade out before it goes. */
const FADE_OUT_MS = 220;

export function HowToPlayCard({ onUnderstood }: { onUnderstood: (dontShowAgain: boolean) => void }) {
  const [dontShow, setDontShow] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const understood = () => {
    if (leaving) return;
    setLeaving(true);
    setTimeout(() => onUnderstood(dontShow), FADE_OUT_MS);
  };

  return (
    <div className={`how-to-play-root${leaving ? ' is-leaving' : ''}`}>
      <div className="panel how-to-play" role="dialog" aria-modal="true" aria-labelledby="how-to-play-text">
        <StarsInProgress />
        <div id="how-to-play-text" className="how-to-play-text">
          <p>Tap letters to spell words.</p>
          <p>The constellations build themselves.</p>
        </div>
        <div className="how-to-play-actions">
          <label className="checkbox">
            <input type="checkbox" checked={dontShow} onChange={(e) => setDontShow(e.target.checked)} />
            <span className="checkbox-box" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M5 12.5l4.5 4.5L19 7.5" />
              </svg>
            </span>
            Don't show this again
          </label>
          <Button variant="primary" onClick={understood} autoFocus>
            Understood
          </Button>
        </div>
      </div>
    </div>
  );
}

/** STARS on the sky, S-T-A-R selected with their dashed lines, a pointer over the last S. */
function StarsInProgress() {
  const last = STARS[STARS.length - 1]!;
  return (
    <svg className="how-to-play-art" viewBox="0 0 344 188" role="img" aria-label="The word STARS as a constellation, S, T, A and R selected, a finger over the last S">
      {STARS.slice(1, SELECTED).map((b, i) => {
        const a = STARS[i]!;
        return <line key={i} className="htp-line" x1={a.x} y1={a.y} x2={b.x} y2={b.y} />;
      })}
      <circle className="htp-ripple" cx={last.x} cy={last.y} r={R} />
      {STARS.map((s, i) => (
        <g key={i} className={i < SELECTED ? 'htp-star is-selected' : 'htp-star'} transform={`translate(${s.x} ${s.y})`}>
          <circle className="htp-disc" r={R} />
          <text className="htp-letter" textAnchor="middle" y={7}>
            {s.letter}
          </text>
        </g>
      ))}
      {/* A pointing hand, its fingertip on the last star. */}
      <g className="htp-hand" transform={`translate(${last.x - 18} ${last.y + 1}) scale(2.5)`}>
        <path d="M10 9.5V4a2 2 0 0 0-4 0v10l-1.6-1.6a2 2 0 0 0-2.8 2.8l3.6 3.6C6.7 20.3 8.4 21 11 21h2a8 8 0 0 0 8-8v-2a2 2 0 0 0-4 0v-1a2 2 0 0 0-4 0v-.5a2 2 0 0 0-4 0z" />
        <path className="htp-hand-crease" d="M10 9.5V12M13 9.6V12M17 11v1.5" />
      </g>
    </svg>
  );
}
